import React from 'react';
import { useQuery } from '@tanstack/react-query';
import useSessionStore from '../state/session';
import { useRoles } from './useRoles';
import { fetchProfileDisplayNames, fetchProfilesByAuthIds } from '../data/userProfiles';
import type { AgreementApprovalViewer } from '../domain/service';

/** The signed-in user as a service agreement quote approver. */
export function useAgreementApprovalViewer(): AgreementApprovalViewer {
  const { isAdmin, isManager, accessRoleCodes } = useRoles();
  const profileId = useSessionStore((s) => s.profileId);
  return React.useMemo(
    () => ({ profileId: profileId ?? null, roleCodes: accessRoleCodes, isManager: isAdmin || isManager }),
    [profileId, accessRoleCodes, isAdmin, isManager],
  );
}

const NO_NAMES = { byProfile: {} as Record<string, string>, byAuth: {} as Record<string, string> };

function uniqueIds(ids: ReadonlyArray<string | null | undefined>): string[] {
  return Array.from(new Set(ids.filter((id): id is string => !!id))).sort();
}

/** Display names for assigned approvers (profile ids) and deciders (auth user ids), in one round trip. */
export function useApprovalPeopleNames(
  profileIds: ReadonlyArray<string | null | undefined>,
  authIds: ReadonlyArray<string | null | undefined> = [],
) {
  const profiles = uniqueIds(profileIds);
  const auths = uniqueIds(authIds);
  const query = useQuery({
    queryKey: ['approval-people-names', profiles, auths],
    enabled: profiles.length + auths.length > 0,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const [byProfile, byAuth] = await Promise.all([
        fetchProfileDisplayNames(profiles),
        fetchProfilesByAuthIds(auths),
      ]);
      if (byProfile.error) throw byProfile.error;
      if (byAuth.error) throw byAuth.error;
      return { byProfile: byProfile.data, byAuth: byAuth.data };
    },
  });
  return query.data ?? NO_NAMES;
}
